import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Inscription, List, useResponsive } from '@common-fe/common-fe';

import AppButton from '@/components/controls/AppButton';
import { ModalWrapper } from '@/components/wrappers';
import { LMNClaim } from '@/modules/MyDocuments/myDocuments.types';
import { useGetClaimServicesQuery } from '@/modules/MyDocuments/queries/useGetClaimServices.query';
import theme from '@/styles/theme';

import LettersOfMedicalNecessityClaimListItem from './LettersOfMedicalNecessityClaimListItem';

interface Props {
  visible: boolean;
  onSetVisible: (visible: boolean) => void;
  claims: LMNClaim[];
  lmnName: string;
}

export const LettersOfMedicalNecessityClaimsModal: React.FC<Props> = ({
  visible,
  onSetVisible,
  claims,
  lmnName,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  
  const serviceIds = useMemo(() => {
    const ids = claims.map(claim => claim.serviceId?.toString()).filter(Boolean) as string[];
    return [...new Set(ids)];
  }, [claims]);
  
  const { services, isLoading } = useGetClaimServicesQuery({ 
    serviceIds,
    enabled: visible
  });

  const getServiceByClaim = useCallback((claim: LMNClaim) => {
    return services.find(s => s.id === claim.serviceId?.toString());
  }, [services]);

  return (
    <ModalWrapper
      title={`Expenses Justified by ${lmnName}`}
      visible={visible}
      onSetVisible={onSetVisible}
      testId="lmn-claims-modal"
      titleBoxStyle={{
        paddingBottom: 0,
      }}
      {...(isMobile
        ? {
            contentStyle: {
              paddingLeft: theme.spacings.spacing8,
              paddingRight: theme.spacings.spacing8,
            },
          }
        : {})}
    >
      <Box direction="column">
        <Inscription margin={{ top: 'spacing12' }} size="medium" textAlign="center">
          Claims listed here use this letter as their medical justification. Links are permanent.
        </Inscription>

        <Box
          margin={{ top: 'spacing24' }}
          background="module"
          round="moduleRound"
          pad={isMobile ? 'spacing12' : 'spacing24'}
        >
          <List>
            {isMobile ? null : (
              <List.Header>
                <List.Header.Item flex={2}></List.Header.Item>
                <List.Header.Item flex={1.2}>{t('Amount')}</List.Header.Item>
                <List.Header.Item flex={0.8}>{t('Date')}</List.Header.Item>
              </List.Header>
            )}

            {claims.map((claim) => (
              <LettersOfMedicalNecessityClaimListItem
                key={claim.claimId}
                claim={claim}
                service={getServiceByClaim(claim)}
                isLoading={isLoading}
                isMobile={isMobile}
              />
            ))}
            
          </List>
        </Box>
        
        <Box direction="row" justify="end" margin={{ top: 'spacing24' }}>
          <AppButton
            buttonType="primary"
            onClick={() => onSetVisible(false)}
            testId="lmn-claims-modal_done-button"
            width='140px'
          >
            {t('Done')}
          </AppButton>
        </Box>
      </Box>
    </ModalWrapper>
  );
};